import React from 'react';
import { motion } from 'framer-motion';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Wine, Utensils, AlertCircle } from 'lucide-react';

const ImportantInfo = () => {
  return (
    <section className="py-16 sm:py-20 bg-background">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-serif mb-4 text-primary">Important Information</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            A few things to know before you arrive
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Alert className="h-full gold-border" data-testid="important-info-byo-alert">
              <Wine className="h-5 w-5 text-primary" />
              <AlertTitle className="text-lg font-serif mb-2">BYO Alcohol & Snacks</AlertTitle>
              <AlertDescription className="text-sm text-muted-foreground">
                Please bring your own alcohol and snacks to enjoy throughout the weekend. 
                Feel free to stock up before arriving at the farm!
              </AlertDescription>
            </Alert>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Alert className="h-full gold-border" data-testid="important-info-meals-alert">
              <Utensils className="h-5 w-5 text-primary" />
              <AlertTitle className="text-lg font-serif mb-2">Meals Provided</AlertTitle>
              <AlertDescription className="text-sm text-muted-foreground">
                Breakfast, lunch, and dinner are provided daily. However, portions are limited, 
                so we recommend bringing extra snacks to keep yourself topped up throughout the day.
              </AlertDescription>
            </Alert>
          </motion.div>
        </div>

        {/* Dietary Restrictions - Highlighted */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-6"
        >
          <Alert 
            className="max-w-4xl mx-auto border-2 border-primary bg-primary/5"
            style={{ 
              backgroundImage: 'linear-gradient(135deg, #F9E27D 0%, #D4AF37 40%, #B8962E 60%, #F6E27A 100%)',
              backgroundSize: '100% 4px',
              backgroundPosition: 'top',
              backgroundRepeat: 'no-repeat',
              paddingTop: '1.5rem'
            }}
          >
            <div className="flex items-start gap-3">
              <Utensils className="h-6 w-6 text-primary mt-1" />
              <div>
                <AlertTitle className="text-xl font-serif mb-2 text-primary">Dietary Restrictions Provided</AlertTitle>
                <AlertDescription className="text-base text-foreground">
                  We accommodate all dietary requirements! Please let us know about any allergies, 
                  vegetarian, vegan, gluten-free, or other dietary needs.
                </AlertDescription>
              </div>
            </div>
          </Alert>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-8 text-center"
        >
          <Alert className="max-w-2xl mx-auto bg-white border-2 border-primary/20 shadow-lg">
            <div className="flex flex-col items-center gap-4">
              <AlertCircle className="h-5 w-5 text-primary" />
              <div className="text-center space-y-2">
                <h3 className="font-serif text-xl font-semibold text-primary">Sherwood Farm</h3>
                <p className="text-sm text-muted-foreground">
                  123-125 Featherstone Road<br/>
                  Sherwood, NSW 2450
                </p>
                <p className="text-xs text-muted-foreground mt-3">
                  <a 
                    href="https://www.sherwoodfarm.com.au/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    Visit Sherwood Farms Website →
                  </a>
                </p>
              </div>
            </div>
          </Alert>
        </motion.div>
      </div>
    </section>
  );
};

export default ImportantInfo;